import { useState, useEffect, useMemo, useRef } from "react";
import { useAuth } from "../App";
import { toast } from "sonner";
import { compareByMonthYear } from "../utils/dateUtils";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Wallet,
  Download,
  RefreshCw,
  Loader2,
  CheckCircle,
  Search,
  ArrowLeft,
  X,
  Printer,
  FileText,
  FileSpreadsheet,
  ClipboardList,
  Save,
  Trash2,
  Bookmark,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { DMYDateInput } from "@/components/ui/dmy-date-picker";

import { formatDMY } from "@/lib/dmy";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// All available columns for print
const ALL_COLUMNS = [
  { key: "tracking", label: "Tracking", default: true },
  { key: "partner_code", label: "Partner", default: true },
  { key: "customer_name", label: "Customer", default: true },
  { key: "order_date", label: "Order Date", default: true },
  { key: "collection_date", label: "Collection Date", default: false },
  { key: "invoice_amount", label: "Invoice Amt", default: true },
  { key: "costing", label: "Costing", default: true },
  { key: "gp", label: "GP", default: true },
  { key: "payment_to_company", label: "To Company", default: true },
  { key: "payment_to_personal", label: "To Personal", default: true },
  { key: "matched_status", label: "Status", default: true },
  { key: "payment_mode", label: "Payment Mode", default: false },
];

const PartnerCollectionsPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  
  // State
  const [collections, setCollections] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [partnerFilter, setPartnerFilter] = useState("");
  const [partnerSuggestions, setPartnerSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [allPartners, setAllPartners] = useState([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const suggestionsRef = useRef(null);
  const inputRef = useRef(null);
  
  // Print Dialog State
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState(
    ALL_COLUMNS.filter(c => c.default).map(c => c.key)
  );
  const [printData, setPrintData] = useState([]);
  const [printTitle, setPrintTitle] = useState("");
  
  // Report Dialog State
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [showReportPreview, setShowReportPreview] = useState(false);
  const [selectedReportTypes, setSelectedReportTypes] = useState([]); // Multi-select
  const [reportSections, setReportSections] = useState([]); // Array of {title, data}
  const [reportTitle, setReportTitle] = useState("");
  const printPreviewRef = useRef(null);
  
  // Saved Templates State
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [showSaveTemplateInput, setShowSaveTemplateInput] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState("");
  const [savingTemplate, setSavingTemplate] = useState(false);
  
  // Available report types
  const REPORT_TYPES = [
    { key: "unmatched", label: "Unmatched Report", color: "orange" },
    { key: "no_invoice", label: "No Invoice Amount Report", color: "red" },
    { key: "negative_gp", label: "Negative GP Report", color: "rose" },
    { key: "high_value", label: "High Value Transactions (≥₹10K)", color: "blue" },
    { key: "company_payments", label: "Company Payments Report", color: "green" },
    { key: "personal_payments", label: "Personal Payments Report", color: "purple" },
  ];
  
  // Sorting state - Default: order_date ascending (oldest first)
  const [sortField, setSortField] = useState("order_date");
  const [sortDirection, setSortDirection] = useState("asc");

  // Handle sort - Click cycle: asc → desc → default (order_date asc)
  const handleSort = (field) => {
    if (sortField === field) {
      if (sortDirection === "asc") {
        // asc → desc
        setSortDirection("desc");
      } else {
        // desc → reset to default (order_date asc)
        setSortField("order_date");
        setSortDirection("asc");
      }
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Get sort icon
  const getSortIcon = (field) => {
    if (sortField !== field) return <span className="text-slate-400 ml-1">↕</span>;
    return sortDirection === "asc" 
      ? <span className="text-cyan-300 ml-1">↑</span> 
      : <span className="text-cyan-300 ml-1">↓</span>;
  };

  // Parse date in ddmmyy format for proper sorting
  const parseDateDDMMYY = (dateStr) => {
    if (!dateStr || dateStr === "-") return 0;
    // Format: ddmmyy (e.g., 040625 = 04 June 2025)
    const str = String(dateStr).padStart(6, '0');
    const day = parseInt(str.substring(0, 2), 10);
    const month = parseInt(str.substring(2, 4), 10);
    const year = parseInt(str.substring(4, 6), 10);
    // Convert to comparable number: yymmdd
    return (year * 10000) + (month * 100) + day;
  };

  // Sorted collections (totals row excluded from sorting)
  const sortedCollections = useMemo(() => {
    if (!collections?.collections?.length) return [];
    
    return [...collections.collections].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      // Special handling for date fields - use monthly sorting
      if (sortField === "order_date" || sortField === "collection_date") {
        return compareByMonthYear(aVal, bVal, sortDirection);
      }
      
      // Handle null/undefined
      if (aVal == null) aVal = "";
      if (bVal == null) bVal = "";
      
      // Numeric comparison for number fields
      if (typeof aVal === "number" || typeof bVal === "number") {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
        return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
      }
      
      // String comparison
      aVal = String(aVal).toLowerCase();
      bVal = String(bVal).toLowerCase();
      if (sortDirection === "asc") {
        return aVal.localeCompare(bVal);
      }
      return bVal.localeCompare(aVal);
    });
  }, [collections, sortField, sortDirection]);

  // Calculate column totals
  const columnTotals = useMemo(() => {
    if (!collections?.collections?.length) return {};
    const items = collections.collections;
    return {
      invoice_amount: items.reduce((sum, c) => sum + (c.invoice_amount || 0), 0),
      costing: items.reduce((sum, c) => sum + (c.costing || 0), 0),
      gp: items.reduce((sum, c) => sum + (c.gp || 0), 0),
      payment_to_company: items.reduce((sum, c) => sum + (c.payment_to_company || 0), 0),
      payment_to_personal: items.reduce((sum, c) => sum + (c.payment_to_personal || 0), 0),
    };
  }, [collections]);

  // Fetch partner list for auto-suggestions
  useEffect(() => {
    const fetchPartners = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/ledger-reconciliation/summary`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          const partners = (data.clients || []).map(c => c.client_code).filter(Boolean);
          setAllPartners(partners);
        }
      } catch (error) {
        console.error("Failed to fetch partners:", error);
      }
    };
    fetchPartners();
  }, [token]);

  // Fetch collections
  const fetchCollections = async () => {
    setLoading(true);
    try {
      let url = `${BACKEND_URL}/api/ledger-reconciliation/partner-collections`;
      const params = new URLSearchParams();
      if (partnerFilter.trim()) params.append("client_code", partnerFilter.trim());
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
      if (statusFilter && statusFilter !== "all") params.append("status", statusFilter);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        setCollections(data);
      } else {
        toast.error(data.detail || "Failed to fetch partner collections");
      }
    } catch (error) {
      toast.error("Failed to fetch partner collections");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchCollections();
  }, []);

  // Handle partner input change with auto-suggestions
  const handlePartnerInputChange = (value) => {
    setPartnerFilter(value);
    
    if (value.length >= 2) {
      const filtered = allPartners.filter(p => 
        p.toLowerCase().includes(value.toLowerCase())
      ).slice(0, 10);
      setPartnerSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
    } else {
      setPartnerSuggestions([]);
      setShowSuggestions(false);
    }
  };

  // Select a suggestion
  const selectPartner = (partner) => {
    setPartnerFilter(partner);
    setShowSuggestions(false);
    // Trigger search immediately
    setTimeout(() => fetchCollections(), 100);
  };

  // Close suggestions on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target) &&
          inputRef.current && !inputRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Open Print Dialog
  const openPrintDialog = () => {
    if (!collections?.collections?.length) {
      toast.error("No data to print");
      return;
    }
    setShowPrintDialog(true);
  };

  // Generate Print Preview
  const generatePrintPreview = () => {
    const data = sortedCollections.map(coll => {
      const row = {};
      selectedColumns.forEach(key => {
        const col = ALL_COLUMNS.find(c => c.key === key);
        if (col) {
          if (key === "matched_status") {
            row[col.label] = coll[key] === "matched" ? "Matched" : "Unmatched";
          } else if (["invoice_amount", "costing", "gp", "payment_to_company", "payment_to_personal"].includes(key)) {
            row[col.label] = coll[key] ? `₹${coll[key].toLocaleString()}` : "-";
          } else {
            row[col.label] = coll[key] || "-";
          }
        }
      });
      return row;
    });
    setPrintData(data);
    setPrintTitle(`Partner Collections${partnerFilter ? ` - ${partnerFilter}` : ""} (${data.length} records)`);
    setShowPrintDialog(false);
    setShowPrintPreview(true);
  };

  // Fetch saved report templates
  const fetchSavedTemplates = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/report-templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSavedTemplates(data.templates || []);
      }
    } catch (error) {
      console.error("Failed to fetch templates:", error);
    }
  };

  // Load templates on mount
  useEffect(() => {
    fetchSavedTemplates();
  }, [token]);

  // Save current selection as template
  const saveAsTemplate = async () => {
    if (!newTemplateName.trim()) {
      toast.error("Please enter a template name");
      return;
    }
    if (selectedReportTypes.length === 0) {
      toast.error("Please select at least one report type");
      return;
    }

    setSavingTemplate(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/report-templates`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          name: newTemplateName.trim(),
          report_types: selectedReportTypes
        })
      });

      if (response.ok) {
        toast.success(`Template "${newTemplateName}" saved!`);
        setNewTemplateName("");
        setShowSaveTemplateInput(false);
        fetchSavedTemplates();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to save template");
      }
    } catch (error) {
      toast.error("Failed to save template");
    } finally {
      setSavingTemplate(false);
    }
  };

  // Delete a saved template
  const deleteTemplate = async (templateId, templateName) => {
    if (!confirm(`Delete template "${templateName}"?`)) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/report-templates/${templateId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success("Template deleted");
        fetchSavedTemplates();
      } else {
        toast.error("Failed to delete template");
      }
    } catch (error) {
      toast.error("Failed to delete template");
    }
  };

  // Load a saved template (apply selection and generate)
  const loadTemplate = (template) => {
    setSelectedReportTypes(template.report_types);
    // Auto-generate preview
    setTimeout(() => {
      const sections = template.report_types.map(type => getReportDataForType(type));
      const totalRecords = sections.reduce((sum, s) => sum + s.data.length, 0);
      setReportSections(sections);
      setReportTitle(`${template.name} (${template.report_types.length} reports, ${totalRecords} total records)`);
      setShowReportDialog(false);
      setShowReportPreview(true);
    }, 100);
  };

  // Open Report Dialog
  const openReportDialog = () => {
    if (!collections?.collections?.length) {
      toast.error("No data for reports");
      return;
    }
    setSelectedReportTypes([]);
    setShowSaveTemplateInput(false);
    setNewTemplateName("");
    setShowReportDialog(true);
  };

  // Toggle report type selection
  const toggleReportType = (key) => {
    setSelectedReportTypes(prev => 
      prev.includes(key) 
        ? prev.filter(k => k !== key)
        : [...prev, key]
    );
  };

  // Get report data for a specific type
  const getReportDataForType = (reportType) => {
    let data = [];
    let title = "";
    
    switch (reportType) {
      case "unmatched":
        data = sortedCollections.filter(c => c.matched_status !== "matched");
        title = `Unmatched Report (${data.length} records)`;
        break;
      case "no_invoice":
        data = sortedCollections.filter(c => !c.invoice_amount || c.invoice_amount === 0);
        title = `No Invoice Amount Report (${data.length} records)`;
        break;
      case "negative_gp":
        data = sortedCollections.filter(c => c.gp < 0);
        title = `Negative GP Report (${data.length} records)`;
        break;
      case "high_value":
        data = sortedCollections.filter(c => c.invoice_amount >= 10000).sort((a, b) => b.invoice_amount - a.invoice_amount);
        title = `High Value Transactions (≥₹10,000) - ${data.length} records`;
        break;
      case "company_payments":
        data = sortedCollections.filter(c => c.payment_to_company > 0);
        title = `Company Payments Report (${data.length} records)`;
        break;
      case "personal_payments":
        data = sortedCollections.filter(c => c.payment_to_personal > 0);
        title = `Personal Payments Report (${data.length} records)`;
        break;
      case "all":
      default:
        data = sortedCollections;
        title = `All Collections Report (${data.length} records)`;
    }
    
    // Format data for display
    const formattedData = data.map(coll => ({
      "Tracking": coll.tracking,
      "Partner": coll.partner_code,
      "Customer": coll.customer_name || "-",
      "Order Date": coll.order_date || "-",
      "Invoice Amt": coll.invoice_amount ? `₹${coll.invoice_amount.toLocaleString()}` : "-",
      "Costing": coll.costing ? `₹${coll.costing.toLocaleString()}` : "-",
      "GP": coll.gp ? `₹${coll.gp.toLocaleString()}` : "-",
      "To Company": coll.payment_to_company ? `₹${coll.payment_to_company.toLocaleString()}` : "-",
      "To Personal": coll.payment_to_personal ? `₹${coll.payment_to_personal.toLocaleString()}` : "-",
      "Status": coll.matched_status === "matched" ? "Matched" : "Unmatched",
    }));
    
    return { title, data: formattedData };
  };

  // Generate single report (quick select)
  const generateSingleReport = (reportType) => {
    const { title, data } = getReportDataForType(reportType);
    setReportSections([{ title, data }]);
    setReportTitle(title);
    setShowReportDialog(false);
    setShowReportPreview(true);
  };

  // Generate multiple selected reports
  const generateSelectedReports = () => {
    if (selectedReportTypes.length === 0) {
      toast.error("Please select at least one report");
      return;
    }
    
    const sections = selectedReportTypes.map(type => getReportDataForType(type));
    const totalRecords = sections.reduce((sum, s) => sum + s.data.length, 0);
    
    setReportSections(sections);
    setReportTitle(`Combined Report (${selectedReportTypes.length} reports, ${totalRecords} total records)`);
    setShowReportDialog(false);
    setShowReportPreview(true);
  };

  // Get count for report type
  const getReportCount = (reportType) => {
    switch (reportType) {
      case "unmatched":
        return collections?.summary?.unmatched_count || 0;
      case "no_invoice":
        return collections?.summary?.invoice_not_found_count || 0;
      case "negative_gp":
        return sortedCollections.filter(c => c.gp < 0).length;
      case "high_value":
        return sortedCollections.filter(c => c.invoice_amount >= 10000).length;
      case "company_payments":
        return sortedCollections.filter(c => c.payment_to_company > 0).length;
      case "personal_payments":
        return sortedCollections.filter(c => c.payment_to_personal > 0).length;
      default:
        return 0;
    }
  };

  // Export combined reports to Excel
  const exportReportsToExcel = () => {
    try {
      const wb = XLSX.utils.book_new();
      
      reportSections.forEach((section, idx) => {
        if (section.data.length > 0) {
          const ws = XLSX.utils.json_to_sheet(section.data);
          const sheetName = section.title.substring(0, 30).replace(/[^a-zA-Z0-9 ]/g, "");
          XLSX.utils.book_append_sheet(wb, ws, sheetName || `Report_${idx + 1}`);
        }
      });

      const dateStr = new Date().toISOString().split("T")[0];
      const filename = `Partner_Reports_${partnerFilter || "All"}_${dateStr}.xlsx`;

      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      saveAs(blob, filename);

      toast.success(`Exported to ${filename}`);
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export");
    }
  };

  // Export to Excel (from preview)
  const exportPreviewToExcel = (data, title) => {
    try {
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Report");

      const maxWidths = {};
      data.forEach(row => {
        Object.keys(row).forEach(key => {
          const value = String(row[key] || "");
          maxWidths[key] = Math.max(maxWidths[key] || key.length, value.length);
        });
      });
      ws["!cols"] = Object.keys(maxWidths).map(key => ({ wch: Math.min(maxWidths[key] + 2, 30) }));

      const dateStr = new Date().toISOString().split("T")[0];
      const filename = `${title.replace(/[^a-zA-Z0-9]/g, "_")}_${dateStr}.xlsx`;

      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      saveAs(blob, filename);

      toast.success(`Exported to ${filename}`);
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export");
    }
  };

  // Print to PDF (browser print)
  const printToPDF = () => {
    window.print();
  };

  // Handle search on Enter key
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      setShowSuggestions(false);
      fetchCollections();
    }
  };

  // Toggle column selection
  const toggleColumn = (key) => {
    setSelectedColumns(prev => 
      prev.includes(key) 
        ? prev.filter(k => k !== key)
        : [...prev, key]
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-cyan-50/30 p-6" data-testid="partner-collections-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate("/ledger-reconciliation")}
            className="hover:bg-slate-200"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <Wallet className="w-7 h-7 text-cyan-600" />
              Partner Collections
            </h1>
            <p className="text-sm text-slate-500">
              Invoice payments received by partners from customers • <span className="text-cyan-600 font-medium">Information Only</span> - Does not affect ledger balance
            </p>
          </div>
        </div>
        <Badge variant="outline" className="bg-cyan-50 text-cyan-700 border-cyan-200 px-3 py-1">
          Info Only
        </Badge>
      </div>

      {/* Filters Card */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-end">
          {/* Partner Code Filter with Auto-suggestions */}
          <div className="relative flex-1 min-w-[200px] max-w-[300px]">
            <Label className="text-sm font-medium text-slate-600 mb-1.5 block">Partner Code</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                ref={inputRef}
                placeholder="Type 2+ chars to search..."
                className="pl-9 pr-8"
                value={partnerFilter}
                onChange={(e) => handlePartnerInputChange(e.target.value)}
                onKeyDown={handleKeyDown}
                onFocus={() => partnerFilter.length >= 2 && partnerSuggestions.length > 0 && setShowSuggestions(true)}
                data-testid="partner-filter-input"
              />
              {partnerFilter && (
                <button
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  onClick={() => {
                    setPartnerFilter("");
                    setShowSuggestions(false);
                  }}
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            
            {/* Auto-suggestions dropdown */}
            {showSuggestions && (
              <div 
                ref={suggestionsRef}
                className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-auto"
              >
                {partnerSuggestions.map((partner, idx) => (
                  <button
                    key={idx}
                    className="w-full px-3 py-2 text-left hover:bg-cyan-50 text-sm font-medium text-slate-700 border-b border-slate-100 last:border-0"
                    onClick={() => selectPartner(partner)}
                  >
                    {partner}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Status Filter */}
          <div className="min-w-[150px]">
            <Label className="text-sm font-medium text-slate-600 mb-1.5 block">Status</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="status-filter">
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="received">Received</SelectItem>
                <SelectItem value="matched">Matched</SelectItem>
                <SelectItem value="unmatched">Unmatched</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Date From */}
          <div className="min-w-[150px]">
            <Label className="text-sm font-medium text-slate-600 mb-1.5 block">From Date</Label>
            <DMYDateInput value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="date-from" />
          </div>

          {/* Date To */}
          <div className="min-w-[150px]">
            <Label className="text-sm font-medium text-slate-600 mb-1.5 block">To Date</Label>
            <DMYDateInput value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="date-to" />
          </div>

          {/* Search Button */}
          <Button 
            onClick={fetchCollections}
            className="bg-cyan-600 hover:bg-cyan-700 gap-2"
            data-testid="search-button"
          >
            <Search className="w-4 h-4" />
            Search
          </Button>

          {/* Refresh Button */}
          <Button 
            variant="outline" 
            onClick={fetchCollections}
            className="gap-2"
            data-testid="refresh-button"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>

          {/* Print Button */}
          <Button 
            variant="outline" 
            onClick={openPrintDialog}
            disabled={!collections?.collections?.length}
            className="gap-2 bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100"
            data-testid="print-button"
          >
            <Printer className="w-4 h-4" />
            Print
          </Button>

          {/* Reports Button */}
          <Button 
            variant="outline" 
            onClick={openReportDialog}
            disabled={!collections?.collections?.length}
            className="gap-2 bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100"
            data-testid="reports-button"
          >
            <ClipboardList className="w-4 h-4" />
            Reports
          </Button>
        </div>
      </div>

      {/* Summary Cards - Compact Size with Original Fonts */}
      {collections?.summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-3 text-center">
            <div className="text-xs text-slate-500 uppercase font-medium mb-1">Total</div>
            <div className="text-xl font-bold text-slate-700">{collections.summary.total_collections?.toLocaleString()}</div>
          </div>
          <div className="bg-gradient-to-br from-cyan-50 to-cyan-100/50 rounded-lg shadow-sm border border-cyan-200 p-3 text-center">
            <div className="text-xs text-cyan-600 uppercase font-medium mb-1">Invoice Amount</div>
            <div className="text-xl font-bold text-cyan-700">₹{collections.summary.total_invoice_amount?.toLocaleString()}</div>
          </div>
          <div className="bg-gradient-to-br from-green-50 to-green-100/50 rounded-lg shadow-sm border border-green-200 p-3 text-center">
            <div className="text-xs text-green-600 uppercase font-medium mb-1">To Company</div>
            <div className="text-xl font-bold text-green-700">₹{collections.summary.total_payment_to_company?.toLocaleString()}</div>
          </div>
          <div className="bg-gradient-to-br from-purple-50 to-purple-100/50 rounded-lg shadow-sm border border-purple-200 p-3 text-center">
            <div className="text-xs text-purple-600 uppercase font-medium mb-1">To Personal</div>
            <div className="text-xl font-bold text-purple-700">₹{collections.summary.total_payment_to_personal?.toLocaleString()}</div>
          </div>
          <div className="bg-emerald-50 rounded-lg shadow-sm border border-emerald-200 p-3 text-center">
            <div className="text-xs text-emerald-600 uppercase font-medium mb-1">Matched</div>
            <div className="text-xl font-bold text-emerald-700">{collections.summary.matched_count}</div>
          </div>
          <div className="bg-orange-50 rounded-lg shadow-sm border border-orange-300 p-3 text-center">
            <div className="text-xs text-orange-600 uppercase font-medium mb-1">Unmatched</div>
            <div className="text-xl font-bold text-orange-700">{collections.summary.unmatched_count}</div>
          </div>
          <div className="bg-red-50 rounded-lg shadow-sm border border-red-300 p-3 text-center">
            <div className="text-xs text-red-600 uppercase font-medium mb-1">No Invoice</div>
            <div className="text-xl font-bold text-red-700">{collections.summary.invoice_not_found_count || 0}</div>
          </div>
        </div>
      )}

      {/* Collections Table */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
            <span className="ml-3 text-slate-500">Loading collections...</span>
          </div>
        ) : collections?.collections?.length > 0 ? (
          <div className="overflow-auto max-h-[calc(100vh-420px)]">
            <table className="w-full text-sm">
              {/* Header Row - Sticky */}
              <thead className="bg-slate-700 text-white sticky top-0 z-20">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold cursor-pointer hover:bg-slate-600" onClick={() => handleSort("tracking")}>
                    Tracking {getSortIcon("tracking")}
                  </th>
                  <th className="px-4 py-3 text-left font-semibold cursor-pointer hover:bg-slate-600" onClick={() => handleSort("partner_code")}>
                    Partner {getSortIcon("partner_code")}
                  </th>
                  <th className="px-4 py-3 text-left font-semibold cursor-pointer hover:bg-slate-600" onClick={() => handleSort("customer_name")}>
                    Customer {getSortIcon("customer_name")}
                  </th>
                  <th className="px-4 py-3 text-center font-semibold cursor-pointer hover:bg-slate-600" onClick={() => handleSort("order_date")}>
                    Order Date {getSortIcon("order_date")}
                  </th>
                  <th className="px-4 py-3 text-center font-semibold cursor-pointer hover:bg-slate-600" onClick={() => handleSort("collection_date")}>
                    Collection Date {getSortIcon("collection_date")}
                  </th>
                  <th className="px-4 py-3 text-right font-semibold text-cyan-300 cursor-pointer hover:bg-slate-600" onClick={() => handleSort("invoice_amount")}>
                    Invoice Amt {getSortIcon("invoice_amount")}
                  </th>
                  <th className="px-4 py-3 text-right font-semibold cursor-pointer hover:bg-slate-600" onClick={() => handleSort("costing")}>
                    Costing {getSortIcon("costing")}
                  </th>
                  <th className="px-4 py-3 text-right font-semibold text-green-300 cursor-pointer hover:bg-slate-600" onClick={() => handleSort("gp")}>
                    GP {getSortIcon("gp")}
                  </th>
                  <th className="px-4 py-3 text-right font-semibold text-blue-300 cursor-pointer hover:bg-slate-600" onClick={() => handleSort("payment_to_company")}>
                    To Company {getSortIcon("payment_to_company")}
                  </th>
                  <th className="px-4 py-3 text-right font-semibold text-purple-300 cursor-pointer hover:bg-slate-600" onClick={() => handleSort("payment_to_personal")}>
                    To Personal {getSortIcon("payment_to_personal")}
                  </th>
                  <th className="px-4 py-3 text-center font-semibold cursor-pointer hover:bg-slate-600" onClick={() => handleSort("matched_status")}>
                    Status {getSortIcon("matched_status")}
                  </th>
                  <th className="px-4 py-3 text-left font-semibold cursor-pointer hover:bg-slate-600" onClick={() => handleSort("payment_mode")}>
                    Payment Mode {getSortIcon("payment_mode")}
                  </th>
                </tr>
                {/* Totals Row - Sticky below header, doesn't sort */}
                <tr className="bg-slate-800 text-white font-bold border-t-2 border-slate-600">
                  <td className="px-4 py-2 text-left" colSpan={5}>
                    <span className="text-slate-300">TOTAL ({sortedCollections.length} records)</span>
                  </td>
                  <td className="px-4 py-2 text-right text-cyan-300">
                    ₹{columnTotals.invoice_amount?.toLocaleString(undefined, {maximumFractionDigits: 2})}
                  </td>
                  <td className="px-4 py-2 text-right text-slate-200">
                    ₹{columnTotals.costing?.toLocaleString(undefined, {maximumFractionDigits: 2})}
                  </td>
                  <td className={`px-4 py-2 text-right ${columnTotals.gp < 0 ? 'text-red-400' : 'text-green-300'}`}>
                    ₹{columnTotals.gp?.toLocaleString(undefined, {maximumFractionDigits: 2})}
                  </td>
                  <td className="px-4 py-2 text-right text-blue-300">
                    ₹{columnTotals.payment_to_company?.toLocaleString(undefined, {maximumFractionDigits: 2})}
                  </td>
                  <td className="px-4 py-2 text-right text-purple-300">
                    ₹{columnTotals.payment_to_personal?.toLocaleString(undefined, {maximumFractionDigits: 2})}
                  </td>
                  <td className="px-4 py-2 text-center" colSpan={2}></td>
                </tr>
              </thead>
              <tbody>
                {sortedCollections.map((coll, idx) => (
                  <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3 font-mono text-blue-600 font-medium">{coll.tracking}</td>
                    <td className="px-4 py-3 font-semibold text-slate-700">{coll.partner_code}</td>
                    <td className="px-4 py-3 text-slate-600">{coll.customer_name || '-'}</td>
                    <td className="px-4 py-3 text-center text-slate-500">{formatDMY(coll.order_date)}</td>
                    <td className="px-4 py-3 text-center text-slate-500">{coll.collection_date || '-'}</td>
                    <td className="px-4 py-3 text-right text-cyan-700 font-medium bg-cyan-50/50">
                      {coll.invoice_amount > 0 ? `₹${coll.invoice_amount?.toLocaleString()}` : '-'}
                    </td>
                    <td className="px-4 py-3 text-right text-slate-600">₹{coll.costing?.toLocaleString()}</td>
                    <td className={`px-4 py-3 text-right font-medium ${coll.gp < 0 ? 'text-red-600 bg-red-50/50' : 'text-green-600 bg-green-50/50'}`}>
                      ₹{coll.gp?.toLocaleString()}
                    </td>
                    <td className={`px-4 py-3 text-right font-medium ${coll.payment_to_company > 0 ? 'text-blue-700 bg-blue-50/50' : 'text-slate-400'}`}>
                      {coll.payment_to_company > 0 ? `₹${coll.payment_to_company?.toLocaleString()}` : '-'}
                    </td>
                    <td className={`px-4 py-3 text-right font-medium ${coll.payment_to_personal > 0 ? 'text-purple-700 bg-purple-50/50' : 'text-slate-400'}`}>
                      {coll.payment_to_personal > 0 ? `₹${coll.payment_to_personal?.toLocaleString()}` : '-'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {coll.matched_status === 'matched' ? (
                        <Badge className="bg-green-100 text-green-700 gap-1">
                          <CheckCircle className="w-3 h-3" /> Matched
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-amber-600 border-amber-300">
                          Unmatched
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-600 text-sm">{coll.payment_mode || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-20 text-slate-500">
            <Wallet className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <p className="text-lg font-medium">No partner collections found</p>
            <p className="text-sm mt-1">Upload partner ledger files to track invoice payments</p>
          </div>
        )}
      </div>

      {/* Footer info */}
      {collections?.collections?.length > 0 && (
        <div className="mt-4 text-sm text-slate-500 text-center">
          Showing {collections.collections.length} collections
          {partnerFilter && ` for partner "${partnerFilter}"`}
        </div>
      )}

      {/* Print Column Selection Dialog */}
      <Dialog open={showPrintDialog} onOpenChange={setShowPrintDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Printer className="w-5 h-5 text-blue-600" />
              Print Options
            </DialogTitle>
            <DialogDescription>
              Select columns to include in print
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="flex justify-between">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setSelectedColumns(ALL_COLUMNS.map(c => c.key))}
              >
                Select All
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setSelectedColumns(ALL_COLUMNS.filter(c => c.default).map(c => c.key))}
              >
                Default
              </Button>
            </div>
            
            <div className="grid grid-cols-2 gap-2 max-h-60 overflow-auto">
              {ALL_COLUMNS.map(col => (
                <div key={col.key} className="flex items-center gap-2">
                  <Checkbox
                    id={col.key}
                    checked={selectedColumns.includes(col.key)}
                    onCheckedChange={() => toggleColumn(col.key)}
                  />
                  <label htmlFor={col.key} className="text-sm cursor-pointer">
                    {col.label}
                  </label>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowPrintDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={generatePrintPreview}
              disabled={selectedColumns.length === 0}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Preview
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Print Preview Dialog */}
      <Dialog open={showPrintPreview} onOpenChange={setShowPrintPreview}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Print Preview
            </DialogTitle>
            <DialogDescription>{printTitle}</DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-auto border rounded-lg print:border-0" ref={printPreviewRef}>
            <table className="w-full text-sm">
              <thead className="bg-slate-100 sticky top-0">
                <tr>
                  {printData[0] && Object.keys(printData[0]).map(key => (
                    <th key={key} className="px-3 py-2 text-left font-semibold border-b">
                      {key}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {printData.map((row, idx) => (
                  <tr key={idx} className="border-b hover:bg-slate-50">
                    {Object.values(row).map((val, i) => (
                      <td key={i} className="px-3 py-2">{val}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex justify-between pt-4 border-t">
            <Button variant="outline" onClick={() => setShowPrintPreview(false)}>
              Back
            </Button>
            <div className="flex gap-2">
              <Button 
                variant="outline"
                onClick={() => exportPreviewToExcel(printData, printTitle)}
                className="gap-2 bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
              >
                <FileSpreadsheet className="w-4 h-4" />
                Excel
              </Button>
              <Button 
                onClick={printToPDF}
                className="gap-2 bg-red-50 border-red-200 text-red-700 hover:bg-red-100"
              >
                <FileText className="w-4 h-4" />
                PDF / Print
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Report Selection Dialog - Multi-Select with Checkboxes */}
      <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-purple-600" />
              Generate Report
            </DialogTitle>
            <DialogDescription>
              Select report types to combine, or use a saved template
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-auto space-y-3 py-4">
            {/* Saved Templates Section */}
            {savedTemplates.length > 0 && (
              <div className="mb-4">
                <div className="text-xs font-semibold text-slate-500 uppercase mb-2 flex items-center gap-1">
                  <Bookmark className="w-3 h-3" />
                  Saved Templates
                </div>
                <div className="space-y-1">
                  {savedTemplates.map((template) => (
                    <div 
                      key={template.id}
                      className="flex items-center gap-2 p-2 border rounded-lg bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200 hover:border-amber-400 transition-colors"
                    >
                      <Button
                        variant="ghost"
                        size="sm"
                        className="flex-1 justify-start h-8 text-left text-amber-800 hover:bg-amber-100"
                        onClick={() => loadTemplate(template)}
                        data-testid={`template-${template.id}`}
                      >
                        <Bookmark className="w-4 h-4 mr-2 text-amber-600" />
                        <span className="flex-1 truncate">{template.name}</span>
                        <Badge variant="outline" className="text-xs bg-white">
                          {template.report_types.length}
                        </Badge>
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-slate-400 hover:text-red-500 hover:bg-red-50"
                        onClick={() => deleteTemplate(template.id, template.name)}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
                <div className="border-b my-3" />
              </div>
            )}

            {/* Multi-select checkboxes */}
            <div className="text-xs font-semibold text-slate-500 uppercase mb-2">
              Select Reports
            </div>
            {REPORT_TYPES.map((report) => (
              <div 
                key={report.key}
                className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                  selectedReportTypes.includes(report.key) 
                    ? 'border-purple-400 bg-purple-50' 
                    : 'border-slate-200 hover:bg-slate-50'
                }`}
                onClick={() => toggleReportType(report.key)}
                data-testid={`report-checkbox-${report.key}`}
              >
                <Checkbox
                  checked={selectedReportTypes.includes(report.key)}
                  onCheckedChange={() => toggleReportType(report.key)}
                />
                <Badge className={`bg-${report.color}-100 text-${report.color}-700`}>
                  {getReportCount(report.key)}
                </Badge>
                <span className="flex-1 text-sm font-medium">{report.label}</span>
              </div>
            ))}
            
            {/* Selection summary & Save Template */}
            {selectedReportTypes.length > 0 && (
              <div className="pt-2 border-t space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-purple-600 font-medium">
                    {selectedReportTypes.length} report{selectedReportTypes.length > 1 ? 's' : ''} selected
                  </span>
                  {!showSaveTemplateInput && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-xs text-amber-600 hover:text-amber-700 hover:bg-amber-50 gap-1"
                      onClick={() => setShowSaveTemplateInput(true)}
                    >
                      <Save className="w-3 h-3" />
                      Save as Template
                    </Button>
                  )}
                </div>
                
                {/* Save Template Input */}
                {showSaveTemplateInput && (
                  <div className="flex items-center gap-2 p-2 bg-amber-50 rounded-lg border border-amber-200">
                    <Input
                      placeholder="Template name..."
                      value={newTemplateName}
                      onChange={(e) => setNewTemplateName(e.target.value)}
                      className="h-8 text-sm flex-1"
                      onKeyDown={(e) => e.key === "Enter" && saveAsTemplate()}
                      autoFocus
                    />
                    <Button
                      size="sm"
                      className="h-8 bg-amber-600 hover:bg-amber-700"
                      onClick={saveAsTemplate}
                      disabled={savingTemplate || !newTemplateName.trim()}
                    >
                      {savingTemplate ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => {
                        setShowSaveTemplateInput(false);
                        setNewTemplateName("");
                      }}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                )}
              </div>
            )}
            
            {/* Quick action: All Collections */}
            <div className="border-t pt-3 mt-3">
              <Button 
                variant="outline"
                className="w-full justify-start gap-3 h-10 text-left text-slate-600"
                onClick={() => generateSingleReport("all")}
                data-testid="report-all-collections"
              >
                <Badge className="bg-slate-100 text-slate-700">{collections?.summary?.total_collections || 0}</Badge>
                Export All Collections (Quick)
              </Button>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={() => setShowReportDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={generateSelectedReports}
              disabled={selectedReportTypes.length === 0}
              className="bg-purple-600 hover:bg-purple-700 gap-2"
              data-testid="generate-reports-button"
            >
              <FileText className="w-4 h-4" />
              Preview ({selectedReportTypes.length})
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Report Preview Dialog - Supports Multiple Sections */}
      <Dialog open={showReportPreview} onOpenChange={setShowReportPreview}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-purple-600" />
              Report Preview
            </DialogTitle>
            <DialogDescription>{reportTitle}</DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-auto border rounded-lg" data-testid="report-preview-content">
            {reportSections.length > 0 ? (
              <div className="space-y-6 p-2">
                {reportSections.map((section, sectionIdx) => (
                  <div key={sectionIdx} className="border rounded-lg overflow-hidden">
                    {/* Section Header */}
                    <div className="bg-slate-700 text-white px-4 py-2 font-semibold text-sm">
                      {section.title}
                    </div>
                    
                    {/* Section Table */}
                    {section.data.length > 0 ? (
                      <table className="w-full text-sm">
                        <thead className="bg-slate-100">
                          <tr>
                            {Object.keys(section.data[0]).map(key => (
                              <th key={key} className="px-3 py-2 text-left font-semibold border-b text-slate-700">
                                {key}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {section.data.map((row, idx) => (
                            <tr key={idx} className="border-b hover:bg-slate-50">
                              {Object.values(row).map((val, i) => (
                                <td key={i} className="px-3 py-2">{val}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <div className="text-center py-6 text-slate-400 text-sm">
                        No records in this section
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-slate-500">
                No data found for this report
              </div>
            )}
          </div>

          <div className="flex justify-between pt-4 border-t">
            <Button variant="outline" onClick={() => {
              setShowReportPreview(false);
              setShowReportDialog(true);
            }}>
              Back to Reports
            </Button>
            <div className="flex gap-2">
              <Button 
                variant="outline"
                onClick={exportReportsToExcel}
                disabled={reportSections.length === 0 || reportSections.every(s => s.data.length === 0)}
                className="gap-2 bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
                data-testid="export-excel-button"
              >
                <FileSpreadsheet className="w-4 h-4" />
                Excel ({reportSections.length} sheet{reportSections.length > 1 ? 's' : ''})
              </Button>
              <Button 
                onClick={printToPDF}
                disabled={reportSections.length === 0 || reportSections.every(s => s.data.length === 0)}
                className="gap-2 bg-red-50 border-red-200 text-red-700 hover:bg-red-100"
                data-testid="export-pdf-button"
              >
                <FileText className="w-4 h-4" />
                PDF / Print
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PartnerCollectionsPage;
